import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, ListSubheader, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { getAutomaticRateAt, getExchangeRateOptions } from '../api/exchangeRate';
import { ExchangeRate } from '../types/exchangeRate';
import { describeRate, toDateTimeLocal } from '../utils/exchangeRates';

const DEFAULT = 'default';
const ON_DATE = 'on-date';

/** Choosing the USD/LKR rate for a payment, invoice or receipt (Phase 104).
 *
 * The default option is whatever applies without a choice (the current
 * automatic rate, or for a receipt the rate its payment was recorded at),
 * so nothing changes unless the user picks something else. Users without
 * exchange_rates:manage only see the default — the server refuses any other
 * rate from them anyway. The chosen rate is reported through `onChange` as
 * the ExchangeRate (null = the default). */
export const ExchangeRateSelect = ({
  value,
  onChange,
  canChoose,
  defaultRate,
  defaultLabel = 'Current automatic rate',
  label = 'USD/LKR exchange rate',
}: {
  value: ExchangeRate | null;
  onChange: (rate: ExchangeRate | null) => void;
  canChoose: boolean;
  /** The rate used when nothing is chosen (shown in the default option). */
  defaultRate?: ExchangeRate | null;
  defaultLabel?: string;
  label?: string;
}) => {
  const options = useQuery({ queryKey: ['exchange-rate-options'], queryFn: getExchangeRateOptions });
  const [mode, setMode] = useState<string>(value ? String(value.id) : DEFAULT);
  const [at, setAt] = useState('');
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [looking, setLooking] = useState(false);

  useEffect(() => {
    if (!value && mode !== ON_DATE) setMode(DEFAULT);
  }, [value, mode]);

  const fallback = defaultRate === undefined ? (options.data?.current ?? null) : defaultRate;
  const defaultText = fallback ? `${defaultLabel} — ${describeRate(fallback)}` : `${defaultLabel} — none available`;

  if (!canChoose) {
    return (
      <Typography variant="body2" color="text.secondary" data-testid="exchange-rate-default">
        <strong>Exchange rate:</strong> {defaultText}
      </Typography>
    );
  }

  const manual = options.data?.manual ?? [];
  const automatic = (options.data?.automatic ?? []).filter((r) => r.id !== options.data?.current?.id);
  const byId = new Map<number, ExchangeRate>(
    [...manual, ...automatic, ...(options.data?.current ? [options.data.current] : [])].map((r) => [r.id, r]),
  );
  // A rate found by date that isn't otherwise listed still needs an option.
  if (value && !byId.has(value.id)) byId.set(value.id, value);

  const lookUp = async () => {
    setLookupError(null);
    if (!at || Number.isNaN(new Date(at).getTime())) {
      setLookupError('Enter a valid date and time.');
      return;
    }
    setLooking(true);
    try {
      const rate = await getAutomaticRateAt(new Date(at).toISOString());
      if (!rate) setLookupError('No automatic rate had been received by that date.');
      else onChange(rate);
    } catch {
      setLookupError("Couldn't look up the rate for that date.");
    } finally {
      setLooking(false);
    }
  };

  return (
    <Stack spacing={1}>
      <TextField
        select
        fullWidth
        size="small"
        label={label}
        value={mode === ON_DATE ? ON_DATE : value ? String(value.id) : DEFAULT}
        onChange={(e) => {
          const next = e.target.value;
          setMode(next);
          setLookupError(null);
          if (next === DEFAULT || next === ON_DATE) onChange(null);
          else onChange(byId.get(Number(next)) ?? null);
        }}
        SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 360 } } } }}
      >
        <MenuItem value={DEFAULT} sx={{ whiteSpace: 'normal' }}>
          {defaultText}
        </MenuItem>
        {options.data?.current && fallback?.id !== options.data.current.id && (
          <MenuItem value={String(options.data.current.id)} sx={{ whiteSpace: 'normal' }}>
            Current automatic rate — {describeRate(options.data.current)}
          </MenuItem>
        )}
        {manual.length > 0 && <ListSubheader>Manual rates</ListSubheader>}
        {manual.map((r) => (
          <MenuItem key={r.id} value={String(r.id)} sx={{ whiteSpace: 'normal' }}>
            {describeRate(r)}
            {r.reason ? ` — ${r.reason}` : ''}
          </MenuItem>
        ))}
        {automatic.length > 0 && <ListSubheader>Earlier automatic rates</ListSubheader>}
        {automatic.map((r) => (
          <MenuItem key={r.id} value={String(r.id)} sx={{ whiteSpace: 'normal' }}>
            {describeRate(r)}
          </MenuItem>
        ))}
        {value && !manual.some((r) => r.id === value.id) && !automatic.some((r) => r.id === value.id) &&
          value.id !== options.data?.current?.id && (
            <MenuItem value={String(value.id)} sx={{ whiteSpace: 'normal' }}>
              {describeRate(value)}
            </MenuItem>
          )}
        <ListSubheader>Other</ListSubheader>
        <MenuItem value={ON_DATE}>Automatic rate on a past date…</MenuItem>
      </TextField>
      {mode === ON_DATE && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-start' }}>
          <TextField
            type="datetime-local"
            size="small"
            label="Date and time"
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: toDateTimeLocal(new Date()) }}
            value={at}
            onChange={(e) => setAt(e.target.value)}
            fullWidth
          />
          <Button variant="outlined" onClick={() => void lookUp()} disabled={looking} sx={{ whiteSpace: 'nowrap' }}>
            {looking ? 'Looking up…' : 'Use rate on this date'}
          </Button>
        </Stack>
      )}
      {mode === ON_DATE && value && (
        <Typography variant="caption" color="text.secondary">
          Using {describeRate(value)}
        </Typography>
      )}
      {lookupError && <Alert severity="warning">{lookupError}</Alert>}
    </Stack>
  );
};
